import { apiFetch } from '@/lib/api/http';

export type PresidentSurveyType = 'start_expectation' | 'growth_evaluation';
export type DisclosureScope = 'member_and_director' | 'director_only' | 'office_only';

export const SURVEY_TYPE_LABELS: Record<PresidentSurveyType, string> = {
  start_expectation: 'START入口',
  growth_evaluation: '成長評価',
};

export const DISCLOSURE_LABELS: Record<DisclosureScope, string> = {
  member_and_director: '本人・理事・サクセスへ開示',
  director_only: '理事・講師のみ',
  office_only: '事務局のみ',
};

/** 固定フィールドのキー（D-146。設問マスタは廃止し、この6項目に固定した） */
export type PresidentFieldKey =
  | 'as_is'
  | 'to_be'
  | 'focus_areas'
  | 'growth_points'
  | 'satisfaction_score'
  | 'challenges';

export const FIELD_LABELS: Record<PresidentFieldKey, string> = {
  as_is: '現在の評価（As is）',
  to_be: '期待する状態（To be）',
  focus_areas: '特に伸ばしてほしい領域',
  growth_points: '成長したと感じる点',
  satisfaction_score: 'プログラムへの満足度（5段階）',
  challenges: '課題と感じる点',
};

/** 種別ごとに使うフィールド */
export const FIELDS_BY_TYPE: Record<PresidentSurveyType, PresidentFieldKey[]> = {
  start_expectation: ['as_is', 'to_be', 'focus_areas'],
  growth_evaluation: ['growth_points', 'satisfaction_score', 'challenges'],
};

export interface PresidentResponseSummary {
  id: string;
  member_id: string;
  member_name: string | null;
  survey_type: PresidentSurveyType;
  survey_round: string;
  answered_at: string | null;
  interviewer_name: string | null;
  disclosure_scope: DisclosureScope;
  source: string;
  created_at: string;
}

export interface PresidentResponseDetail extends PresidentResponseSummary {
  as_is: string | null;
  to_be: string | null;
  focus_areas: string | null;
  growth_points: string | null;
  satisfaction_score: number | null;
  challenges: string | null;
}

export interface PresidentResponseInput {
  survey_round?: string;
  answered_at?: string;
  interviewer_name?: string;
  disclosure_scope?: DisclosureScope;
  as_is?: string | null;
  to_be?: string | null;
  focus_areas?: string | null;
  growth_points?: string | null;
  satisfaction_score?: number | null;
  challenges?: string | null;
}

export function fetchPresidentResponses(memberId?: string): Promise<PresidentResponseSummary[]> {
  const query = memberId ? `?member_id=${memberId}` : '';
  return apiFetch<PresidentResponseSummary[]>(`/president-surveys${query}`);
}

export function fetchPresidentResponse(responseId: string): Promise<PresidentResponseDetail> {
  return apiFetch<PresidentResponseDetail>(`/president-surveys/${responseId}`);
}

/** 入力済みの実施回ラベル一覧（入力フォームの候補用 / 事務局のみ） */
export function fetchPresidentRoundLabels(): Promise<string[]> {
  return apiFetch<string[]>('/president-surveys/rounds');
}

export function createPresidentResponse(
  payload: PresidentResponseInput & {
    member_id: string;
    survey_type: PresidentSurveyType;
    survey_round: string;
    answered_at: string;
    interviewer_name: string;
  },
): Promise<PresidentResponseSummary> {
  return apiFetch<PresidentResponseSummary>('/president-surveys', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
}

export function updatePresidentResponse(
  responseId: string,
  payload: PresidentResponseInput,
): Promise<PresidentResponseDetail> {
  return apiFetch<PresidentResponseDetail>(`/president-surveys/${responseId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
}

export function deletePresidentResponse(responseId: string): Promise<void> {
  return apiFetch<void>(`/president-surveys/${responseId}`, { method: 'DELETE' });
}
